/**
 * High-quality lobby sound design — cinematic orchestral fantasy RPG.
 *
 * These replace the earlier "AAA game UI" palette with warmer, more
 * orchestrally-textured cues that sit next to the lobby playlist
 * (A New Dawn, The Fading Kingdom, Whispers of a Forgotten Realm, etc.).
 * The common ingredients are harp-style plucks, choral / string pads,
 * bowed-metal bells, brass horn hits and cathedral-sized reverb.
 *
 * `hqPlayBuyout` intentionally keeps its original "tile shattering"
 * design — the tumbling-rocks sequence reads correctly against the
 * shake + debris animation, and direct feedback was that it's the one
 * sound that shouldn't change.
 *
 * All other cues were rebuilt with:
 *   - slower, softer attacks (no chiptune brightness)
 *   - richer harmonic stacks (thirds + fifths of a warm key centre)
 *   - longer decays that breathe through the hall convolver
 *   - bowed / choral / brass voicings in place of plain sines/squares
 *
 * Implementation uses Web Audio API with a single shared convolution
 * reverb + gentle master bus compressor so the whole palette lives in
 * the same acoustic space. Functions respect `isSoundMuted()` from
 * `@/lib/sounds` so the mute toggle in the header works uniformly.
 */

import { isSoundMuted } from '@/lib/sounds';

// ─── Shared audio graph ─────────────────────────────────────────────────────

let ctx: AudioContext | null = null;
let masterBus: GainNode | null = null;
let drySend: GainNode | null = null;
let wetSend: GainNode | null = null;
let verb: ConvolverNode | null = null;
let compressor: DynamicsCompressorNode | null = null;
let initialized = false;

function initGraph() {
  if (initialized || typeof window === 'undefined') return;
  try {
    ctx = new AudioContext();
    masterBus = ctx.createGain();
    masterBus.gain.value = 0.9;

    compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -14;
    compressor.knee.value = 18;
    compressor.ratio.value = 3;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.12;

    drySend = ctx.createGain();
    drySend.gain.value = 1.0;
    wetSend = ctx.createGain();
    // Slightly wetter default than the old palette — these sounds want to
    // sit in a cathedral, not a booth. Per-voice `reverb` multipliers
    // refine around this baseline.
    wetSend.gain.value = 0.4;

    verb = ctx.createConvolver();
    // Longer tail (2.2s) for the orchestral aesthetic. The pre-delay
    // bundled in `buildImpulse('hall')` keeps the early reflections
    // distinct so percussive voices don't smear.
    verb.buffer = buildImpulse(ctx, 2.2, 3.0, 'hall');

    drySend.connect(compressor);
    wetSend.connect(verb).connect(compressor);
    compressor.connect(masterBus).connect(ctx.destination);

    initialized = true;
  } catch {
    initialized = false;
    ctx = null;
  }
}

/** Call from a user gesture to unlock audio. */
export function ensureHqAudioReady() {
  initGraph();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {
      // non-critical
    });
  }
}

function getCtx(): AudioContext | null {
  if (!initialized) initGraph();
  if (!ctx) return null;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {
      // non-critical
    });
  }
  return ctx;
}

function buildImpulse(
  context: AudioContext,
  seconds: number,
  decay: number,
  space: 'room' | 'hall' = 'hall',
): AudioBuffer {
  const rate = context.sampleRate;
  const length = Math.round(rate * seconds);
  const buf = context.createBuffer(2, length, rate);
  const predelay = Math.round(rate * (space === 'hall' ? 0.015 : 0.004));
  for (let c = 0; c < 2; c++) {
    const data = buf.getChannelData(c);
    for (let i = 0; i < length; i++) {
      if (i < predelay) {
        data[i] = 0;
      } else {
        const n = (i - predelay) / length;
        // Dense noise with exponential decay, gently tilted toward low-mid
        const noise = (Math.random() * 2 - 1);
        const env = Math.pow(1 - n, decay);
        const tilt = 0.8 + 0.2 * Math.sin((i / rate) * 40 * (c === 0 ? 1.0 : 1.03));
        data[i] = noise * env * tilt;
      }
    }
  }
  return buf;
}

interface VoiceOptions {
  /** Stereo pan (-1 left, 0 center, 1 right). */
  pan?: number;
  /** Wet reverb send amount (0..1), added to the global wet. */
  reverb?: number;
}

function voiceOut(options: VoiceOptions = {}): { node: AudioNode; wet: AudioNode } | null {
  if (!ctx || !drySend || !wetSend) return null;
  const panner = ctx.createStereoPanner();
  panner.pan.value = options.pan ?? 0;

  const dry = ctx.createGain();
  dry.gain.value = 1.0;
  const wet = ctx.createGain();
  wet.gain.value = options.reverb ?? 0.55;

  panner.connect(dry).connect(drySend);
  panner.connect(wet).connect(wetSend);

  return { node: panner, wet };
}

function noiseBuffer(context: AudioContext, seconds: number): AudioBuffer {
  const length = Math.round(context.sampleRate * seconds);
  const buf = context.createBuffer(1, length, context.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

function envelopeGain(
  context: AudioContext,
  start: number,
  attack: number,
  peak: number,
  hold: number,
  decay: number,
): GainNode {
  const g = context.createGain();
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), start + attack);
  g.gain.setValueAtTime(Math.max(0.0001, peak), start + attack + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, start + attack + hold + decay);
  return g;
}

// ─── Palette primitives ────────────────────────────────────────────────────
//
// These small helpers build the recurring orchestral voicings used
// throughout the palette so each cue can layer them into a motif
// without duplicating the underlying synthesis. They deliberately avoid
// bright / pingy partials — the intent is warm, plucked / bowed / blown
// tones you'd hear in a fantasy RPG UI (A New Dawn, The Fading Kingdom).

/**
 * Karplus-Strong-ish harp pluck using a quick-decaying triangle + the
 * first inharmonic partial. Short attack, medium decay, fairly dry —
 * harps in fantasy cues usually sit up-front with a small early
 * reflection rather than a long tail.
 */
function harpPluck(
  c: AudioContext,
  out: AudioNode,
  freq: number,
  startAt: number,
  gain: number,
  decay: number = 0.55,
) {
  const body = c.createOscillator();
  body.type = 'triangle';
  body.frequency.setValueAtTime(freq, startAt);
  const bodyLp = c.createBiquadFilter();
  bodyLp.type = 'lowpass';
  bodyLp.frequency.value = Math.min(6000, freq * 6);
  const bodyEnv = envelopeGain(c, startAt, 0.004, gain, 0.01, decay);
  body.connect(bodyLp).connect(bodyEnv).connect(out);
  body.start(startAt);
  body.stop(startAt + decay + 0.1);

  // Second partial — soft sine one octave up, faster decay, adds shimmer.
  const part = c.createOscillator();
  part.type = 'sine';
  part.frequency.setValueAtTime(freq * 2.01, startAt);
  const partEnv = envelopeGain(c, startAt, 0.005, gain * 0.22, 0.005, decay * 0.6);
  part.connect(partEnv).connect(out);
  part.start(startAt);
  part.stop(startAt + decay + 0.1);
}

/**
 * Slow-swelling string / choir pad — two detuned sawtooths through a
 * gentle lowpass, shaped with a long attack. Stackable: call for each
 * chord tone you want sustained.
 */
function stringBow(
  c: AudioContext,
  out: AudioNode,
  freq: number,
  startAt: number,
  gain: number,
  attack: number = 0.18,
  sustain: number = 0.5,
  release: number = 0.6,
) {
  [-7, 7].forEach((cents) => {
    const osc = c.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(freq, startAt);
    osc.detune.value = cents;
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(freq * 2.2, startAt);
    lp.frequency.exponentialRampToValueAtTime(freq * 3.6, startAt + attack);
    lp.Q.value = 0.7;
    const env = envelopeGain(c, startAt, attack, gain * 0.5, sustain, release);
    osc.connect(lp).connect(env).connect(out);
    osc.start(startAt);
    osc.stop(startAt + attack + sustain + release + 0.05);
  });
}

/**
 * Warm French-horn style stab — rounded brass with a fast attack and
 * medium decay. Uses a sawtooth through a heavy lowpass + soft saturation
 * for body. Feels like a brass section breathing rather than a synth.
 */
function brassHorn(
  c: AudioContext,
  out: AudioNode,
  freq: number,
  startAt: number,
  gain: number,
  duration: number = 0.35,
) {
  const osc = c.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, startAt);

  const shaper = c.createWaveShaper();
  const curve = new Float32Array(128);
  for (let i = 0; i < 128; i++) {
    const x = (i / 64) - 1;
    curve[i] = Math.tanh(x * 1.6);
  }
  shaper.curve = curve;
  shaper.oversample = '2x';

  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(freq * 2.8, startAt);
  lp.frequency.exponentialRampToValueAtTime(freq * 4.2, startAt + duration * 0.3);
  lp.Q.value = 0.9;

  const env = envelopeGain(c, startAt, 0.035, gain, duration * 0.5, duration * 0.6);
  osc.connect(shaper).connect(lp).connect(env).connect(out);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.2);

  // Sub octave for body — sine one octave down, gentle.
  const sub = c.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(freq / 2, startAt);
  const subEnv = envelopeGain(c, startAt, 0.04, gain * 0.35, duration * 0.5, duration * 0.6);
  sub.connect(subEnv).connect(out);
  sub.start(startAt);
  sub.stop(startAt + duration + 0.2);
}

/**
 * Rounded marimba / wooden mallet — a warm percussive tone with a
 * soft "tok" attack and a short *bell-less* decay. Body is a single
 * sine (no inharmonic partials), so the tail is woody rather than
 * shimmery. A lowpassed noise burst at the front sells the felt-on-
 * wood transient without any metallic sparkle.
 *
 * Used by `hqPlayBuyoutConfirm` instead of the previous bell crown
 * because the high-bell partials read as a piercing "bling" against
 * the buyout's settling dust tail. This primitive caps below ~2 kHz
 * audible energy and has no inharmonic ringing, so it sits comfortably
 * in the cathedral mix.
 */
function softMallet(
  c: AudioContext,
  out: AudioNode,
  freq: number,
  startAt: number,
  gain: number,
  decay: number = 0.34,
) {
  // Wood "tok" — short noise burst lowpassed into the wood register.
  // Adds the percussive transient without any high frequencies.
  const tok = c.createBufferSource();
  tok.buffer = noiseBuffer(c, 0.014);
  const tokLp = c.createBiquadFilter();
  tokLp.type = 'lowpass';
  tokLp.frequency.value = 1700;
  tokLp.Q.value = 0.7;
  const tokEnv = envelopeGain(c, startAt, 0.0006, gain * 0.5, 0.0, 0.022);
  tok.connect(tokLp).connect(tokEnv).connect(out);
  tok.start(startAt);
  tok.stop(startAt + 0.04);

  // Body — single sine that decays smoothly. No 2.01×/3.02× partials,
  // so absolutely no bell ring; the tone is round and warm.
  const body = c.createOscillator();
  body.type = 'sine';
  body.frequency.setValueAtTime(freq, startAt);
  const bodyEnv = envelopeGain(c, startAt, 0.005, gain, 0.018, decay);
  body.connect(bodyEnv).connect(out);
  body.start(startAt);
  body.stop(startAt + decay + 0.08);

  // Faint sub-octave thickener — sine 12 semitones below at low gain
  // so the mallet has weight without becoming a thump. No effect on
  // perceived pitch, just adds warmth.
  const warm = c.createOscillator();
  warm.type = 'sine';
  warm.frequency.setValueAtTime(freq * 0.5, startAt);
  const warmEnv = envelopeGain(c, startAt, 0.005, gain * 0.18, 0.012, decay * 0.7);
  warm.connect(warmEnv).connect(out);
  warm.start(startAt);
  warm.stop(startAt + decay + 0.08);
}

/**
 * Bowed-metal bell with inharmonic partials — fundamental + three
 * partials at 2.01×, 3.02× and 4.17× (approximating a church bell's
 * modes). Long decay, warm attack, very little noise — feels ceremonial.
 */
function softBell(
  c: AudioContext,
  out: AudioNode,
  freq: number,
  startAt: number,
  gain: number,
  decay: number = 1.2,
) {
  const partials: Array<[number, number]> = [
    [1, 1.0],
    [2.01, 0.48],
    [3.02, 0.18],
    [4.17, 0.1],
  ];
  partials.forEach(([ratio, amp]) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq * ratio, startAt);
    const env = envelopeGain(
      c,
      startAt,
      0.008,
      gain * amp,
      0.02,
      decay * (1 - (ratio - 1) * 0.15),
    );
    osc.connect(env).connect(out);
    osc.start(startAt);
    osc.stop(startAt + decay + 0.2);
  });
}

// ─── Seat click — leather-bound tome flick ──────────────────────────────────

/**
 * Warm "page turn" click — a muted wood / parchment tick with a soft
 * sub-thump and a short resonant tail, like flipping through a gilded
 * inventory book in a fantasy UI. Light reverb so it still reads as a
 * UI cue, but sits in the same room as everything else.
 */
export function hqPlaySeatClick() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ pan: (Math.random() - 0.5) * 0.18, reverb: 0.18 });
  if (!out) return;
  const now = c.currentTime;

  // 1. Wood tick — bandpassed noise (~1.5 kHz) for the parchment crackle.
  const tick = c.createBufferSource();
  tick.buffer = noiseBuffer(c, 0.03);
  const tickBp = c.createBiquadFilter();
  tickBp.type = 'bandpass';
  tickBp.frequency.value = 1600;
  tickBp.Q.value = 1.5;
  const tickEnv = envelopeGain(c, now, 0.001, 0.32, 0.0, 0.024);
  tick.connect(tickBp).connect(tickEnv).connect(out.node);
  tick.start(now);
  tick.stop(now + 0.04);

  // 2. Wood body — short sine thump around 180 Hz gives physicality
  //    without the classic-app's metallic hardness.
  const body = c.createOscillator();
  body.type = 'sine';
  body.frequency.setValueAtTime(180, now);
  body.frequency.exponentialRampToValueAtTime(120, now + 0.06);
  const bodyEnv = envelopeGain(c, now, 0.003, 0.42, 0.01, 0.085);
  body.connect(bodyEnv).connect(out.node);
  body.start(now);
  body.stop(now + 0.12);

  // 3. Gentle resonance — triangle harmonic at ~580 Hz rings for ~80 ms.
  const ring = c.createOscillator();
  ring.type = 'triangle';
  ring.frequency.setValueAtTime(580, now);
  const ringEnv = envelopeGain(c, now, 0.005, 0.08, 0.0, 0.08);
  ring.connect(ringEnv).connect(out.node);
  ring.start(now);
  ring.stop(now + 0.1);
}

// ─── Bulk select — soft wood chime ──────────────────────────────────────────

/**
 * Lighter version of the seat click for basket / multi-select actions.
 * A bandpassed wood tick + a quick harp pluck that walks through the
 * scale on consecutive picks (via the randomised pitch), so rapid
 * selections feel like a tiny melody rather than a single clack.
 */
export function hqPlayBulkSelect() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ pan: (Math.random() - 0.5) * 0.35, reverb: 0.32 });
  if (!out) return;
  const now = c.currentTime;

  // Wood tick (gentler than seat-click so chained picks don't grate).
  const tick = c.createBufferSource();
  tick.buffer = noiseBuffer(c, 0.02);
  const tickBp = c.createBiquadFilter();
  tickBp.type = 'bandpass';
  tickBp.frequency.value = 1800;
  tickBp.Q.value = 1.4;
  const tickEnv = envelopeGain(c, now, 0.0008, 0.22, 0.0, 0.018);
  tick.connect(tickBp).connect(tickEnv).connect(out.node);
  tick.start(now);
  tick.stop(now + 0.04);

  // Pentatonic harp pick — one of a set of warm fantasy-friendly pitches
  // so each successive click walks an implied melody. 880 Hz ≈ A5.
  const pentatonic = [784, 880, 988, 1175, 1318]; // G5, A5, B5, D6, E6
  const freq = pentatonic[Math.floor(Math.random() * pentatonic.length)];
  harpPluck(c, out.node, freq, now + 0.003, 0.22, 0.35);
}

// ─── Mint — invocation: choir swell → harp arpeggio → bell toll ─────────────

/**
 * Feels like the moment a new seat is summoned into the realm. A choral
 * string pad swells under a rising C-major harp arpeggio, resolving on a
 * bell toll one octave up. Long cathedral tail.
 */
export function hqPlayMint() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.85 });
  if (!out) return;
  const now = c.currentTime;

  // Choral string pad — C/E/G swells from 0 → peak over 350ms.
  [261.63, 329.63, 392.0].forEach((f, i) => {
    stringBow(c, out.node, f, now, 0.28 - i * 0.04, 0.28, 0.55, 0.75);
  });

  // Harp arpeggio — C5, E5, G5, C6 cascading into the bell.
  const arp = [523.25, 659.25, 783.99, 1046.5];
  arp.forEach((f, i) => {
    harpPluck(c, out.node, f, now + 0.08 + i * 0.065, 0.38 - i * 0.04, 0.65);
  });

  // Ceremonial bell toll on C6, just after the top note of the arp.
  softBell(c, out.node, 1046.5, now + 0.38, 0.4, 1.6);
}

// ─── Claim — descending light shaft → bell resolve ──────────────────────────

/**
 * Lines up with the claim visual (light shaft settling on the tile). A
 * gentle airy descent tapers into a warm bell + harp on G5, so the cue
 * "lands" on the tile rather than skyrockets off of it.
 */
export function hqPlayClaim() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.7 });
  if (!out) return;
  const now = c.currentTime;

  // Soft air descent — filtered noise dropping in pitch, evokes a beam
  // of light settling. Low Q so it reads as "air" rather than a filter
  // sweep.
  const air = c.createBufferSource();
  air.buffer = noiseBuffer(c, 0.5);
  const airBp = c.createBiquadFilter();
  airBp.type = 'bandpass';
  airBp.frequency.setValueAtTime(3600, now);
  airBp.frequency.exponentialRampToValueAtTime(900, now + 0.42);
  airBp.Q.value = 0.9;
  const airEnv = envelopeGain(c, now, 0.03, 0.18, 0.1, 0.32);
  air.connect(airBp).connect(airEnv).connect(out.node);
  air.start(now);
  air.stop(now + 0.55);

  // Bell on G5 — the tile "accepts" the claim. Slight delay so the air
  // lands into it.
  softBell(c, out.node, 783.99, now + 0.3, 0.32, 1.1);

  // Small harp confirmation — perfect fifth above the bell (D6) played
  // right after, for a lifting resolution.
  harpPluck(c, out.node, 1174.66, now + 0.44, 0.24, 0.5);
}

// ─── Buyout — shake → crack → tumbling rocks → dust rumble ──────────────────

/**
 * Timed to line up with the tile buyout animation:
 *   0.00 → 0.26  shake  → low tension rumble + growing gravel noise
 *   0.26          crack  → sharp impact transient (tile splits)
 *   0.26 → 1.00  tumble → 5 irregular rock impacts + tumbling whoosh
 *   1.00 → 1.60  decay  → sub rumble + dust tail
 */
export function hqPlayBuyout() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.55 });
  if (!out) return;
  const now = c.currentTime;

  const SHAKE_END = 0.26;

  // ── Shake: low rumble that grows into the crack ─────────────────────────
  const rumbleA = c.createOscillator();
  rumbleA.type = 'sine';
  rumbleA.frequency.setValueAtTime(48, now);
  rumbleA.frequency.exponentialRampToValueAtTime(36, now + SHAKE_END);
  const rumbleAEnv = envelopeGain(c, now, 0.03, 0.42, SHAKE_END - 0.08, 0.02);
  rumbleA.connect(rumbleAEnv).connect(out.node);
  rumbleA.start(now);
  rumbleA.stop(now + SHAKE_END + 0.04);

  // Vibrato to simulate the rattle of dust shaking loose
  const vib = c.createBufferSource();
  vib.buffer = noiseBuffer(c, SHAKE_END + 0.02);
  const vibBp = c.createBiquadFilter();
  vibBp.type = 'bandpass';
  vibBp.frequency.setValueAtTime(240, now);
  vibBp.frequency.exponentialRampToValueAtTime(680, now + SHAKE_END);
  vibBp.Q.value = 1.8;
  const vibEnv = c.createGain();
  vibEnv.gain.setValueAtTime(0.0001, now);
  vibEnv.gain.exponentialRampToValueAtTime(0.3, now + SHAKE_END * 0.8);
  vibEnv.gain.exponentialRampToValueAtTime(0.0001, now + SHAKE_END + 0.02);
  vib.connect(vibBp).connect(vibEnv).connect(out.node);
  vib.start(now);
  vib.stop(now + SHAKE_END + 0.05);

  // ── Crack: sharp transient at the break point ───────────────────────────
  const crack = c.createBufferSource();
  crack.buffer = noiseBuffer(c, 0.04);
  const crackBp = c.createBiquadFilter();
  crackBp.type = 'bandpass';
  crackBp.frequency.value = 2400;
  crackBp.Q.value = 1.4;
  const crackEnv = envelopeGain(c, now + SHAKE_END, 0.001, 0.85, 0.0, 0.06);
  crack.connect(crackBp).connect(crackEnv).connect(out.node);
  crack.start(now + SHAKE_END);
  crack.stop(now + SHAKE_END + 0.08);

  // A shaped low-mid body to the crack — the "thump" of stone splitting
  const crackBody = c.createOscillator();
  crackBody.type = 'sawtooth';
  crackBody.frequency.setValueAtTime(160, now + SHAKE_END);
  crackBody.frequency.exponentialRampToValueAtTime(52, now + SHAKE_END + 0.22);
  const shaper = c.createWaveShaper();
  const curve = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const x = (i / 128) - 1;
    curve[i] = Math.tanh(x * 2.2);
  }
  shaper.curve = curve;
  shaper.oversample = '2x';
  const crackLp = c.createBiquadFilter();
  crackLp.type = 'lowpass';
  crackLp.frequency.setValueAtTime(1100, now + SHAKE_END);
  crackLp.frequency.exponentialRampToValueAtTime(340, now + SHAKE_END + 0.22);
  const crackBodyEnv = envelopeGain(c, now + SHAKE_END, 0.004, 0.7, 0.03, 0.18);
  crackBody.connect(shaper).connect(crackLp).connect(crackBodyEnv).connect(out.node);
  crackBody.start(now + SHAKE_END);
  crackBody.stop(now + SHAKE_END + 0.3);

  // ── Tumble: irregular rock impacts over 0.26 → 1.00 ─────────────────────
  const impactTimes = [0.00, 0.12, 0.22, 0.34, 0.48, 0.62];
  impactTimes.forEach((offset, i) => {
    const t0 = now + SHAKE_END + offset;
    const pan = ((i * 0.618) % 1) - 0.5;
    const imp = c.createBufferSource();
    imp.buffer = noiseBuffer(c, 0.05);
    const impBp = c.createBiquadFilter();
    impBp.type = 'bandpass';
    impBp.frequency.value = 600 + ((i * 311) % 700);
    impBp.Q.value = 1.6;
    const impPanner = c.createStereoPanner();
    impPanner.pan.value = pan * 0.6;
    const impEnv = envelopeGain(c, t0, 0.002, 0.45 - i * 0.045, 0.0, 0.12);
    imp.connect(impBp).connect(impEnv).connect(impPanner).connect(out.node);
    imp.start(t0);
    imp.stop(t0 + 0.16);

    // Each impact gets a low thump body
    const thump = c.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(120 - i * 8, t0);
    thump.frequency.exponentialRampToValueAtTime(45 - i * 3, t0 + 0.18);
    const thumpEnv = envelopeGain(c, t0, 0.003, 0.3 - i * 0.035, 0.01, 0.18);
    thump.connect(thumpEnv).connect(impPanner).connect(out.node);
    thump.start(t0);
    thump.stop(t0 + 0.22);
  });

  // Tumbling whoosh that underscores the falling rocks
  const tumble = c.createBufferSource();
  tumble.buffer = noiseBuffer(c, 0.8);
  const tumbleBp = c.createBiquadFilter();
  tumbleBp.type = 'bandpass';
  tumbleBp.frequency.setValueAtTime(900, now + SHAKE_END);
  tumbleBp.frequency.exponentialRampToValueAtTime(220, now + SHAKE_END + 0.75);
  tumbleBp.Q.value = 0.8;
  const tumbleEnv = envelopeGain(c, now + SHAKE_END, 0.02, 0.22, 0.35, 0.35);
  tumble.connect(tumbleBp).connect(tumbleEnv).connect(out.node);
  tumble.start(now + SHAKE_END);
  tumble.stop(now + SHAKE_END + 0.9);

  // ── Decay: sub rumble tail + dust hiss ──────────────────────────────────
  const sub = c.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(46, now + SHAKE_END);
  sub.frequency.exponentialRampToValueAtTime(28, now + 1.2);
  const subEnv = envelopeGain(c, now + SHAKE_END, 0.03, 0.5, 0.5, 0.4);
  sub.connect(subEnv).connect(out.node);
  sub.start(now + SHAKE_END);
  sub.stop(now + 1.4);

  const dust = c.createBufferSource();
  dust.buffer = noiseBuffer(c, 0.5);
  const dustLp = c.createBiquadFilter();
  dustLp.type = 'lowpass';
  dustLp.frequency.value = 1800;
  const dustEnv = envelopeGain(c, now + 1.0, 0.1, 0.14, 0.2, 0.35);
  dust.connect(dustLp).connect(dustEnv).connect(out.node);
  dust.start(now + 1.0);
  dust.stop(now + 1.6);
}

// ─── Buyout confirm — warm wood-mallet "ta-da" landing ──────────────────────

/**
 * Positive "the new owner has arrived" cue. Fires ~1.22s after
 * `hqPlayBuyout` (i.e. at `BUYOUT_TILE_VISIBLE_AT`) when the regrown
 * slab finishes its grow-back and the new avatar fades in. Companion
 * to the green ring flash on `TileAnimHighlightOverlay` (3D) and
 * `.seat-buyout-animation` (2D).
 *
 * Voicing is two soft mallet hits a perfect-fourth apart (A4 → D5),
 * the classic "ta-da" interval rendered in warm marimba-style wood
 * rather than the previous brass-triad / harp-arp / high-bell stack.
 * The earlier design read as a piercing "bling" against the still-
 * decaying dust tail because the bell's 2.01×/3.02× partials at A5
 * (= A6 partials) sit right where the ear is most sensitive. Mallet
 * bodies are pure sines with no inharmonic content, capped under
 * ~2 kHz, so the cue is positive and playful without any chime sting.
 *
 * Underpinned by a single short pizzicato on D3 (so the cue has body
 * without leaning on the buyout's sub rumble) and a faint two-note
 * brass breath in the low-mid (D3 + F#3) that gives the mallets an
 * orchestral context. Reverb is dialled back from `0.7 → 0.42` so the
 * landing feels "in your hand" (signing the deed) rather than
 * cathedral-ceremonial — which matches the lighter mood the new
 * voicing is aiming for. Total cue ≈ 600 ms including tail.
 */
export function hqPlayBuyoutConfirm() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.42 });
  if (!out) return;
  const now = c.currentTime;

  // Two-note "ta-da" — A4 → D5, perfect fourth. Soft mallet voicing,
  // second note a touch louder so the lift pops without spiking.
  // ~85 ms gap reads as a deliberate two-step rather than a chord.
  softMallet(c, out.node, 440.0, now, 0.32, 0.34); // A4
  softMallet(c, out.node, 587.33, now + 0.085, 0.36, 0.42); // D5

  // Pizzicato bass — single damped pluck on D3. Triangle through a
  // tight lowpass so it reads as orchestral pizz (gut-string thump),
  // not a synth sub. Grounds the mallets and gives the cue body
  // without competing with the buyout's residual dust rumble.
  const pizz = c.createOscillator();
  pizz.type = 'triangle';
  pizz.frequency.setValueAtTime(146.83, now); // D3
  const pizzLp = c.createBiquadFilter();
  pizzLp.type = 'lowpass';
  pizzLp.frequency.value = 620;
  pizzLp.Q.value = 0.6;
  const pizzEnv = envelopeGain(c, now, 0.004, 0.34, 0.018, 0.26);
  pizz.connect(pizzLp).connect(pizzEnv).connect(out.node);
  pizz.start(now);
  pizz.stop(now + 0.34);

  // Warm horn breath — D3 + F#3, very low gain so the mallets stay
  // out front. Reads as a confident "yes" exhale from the brass
  // section rather than a fanfare; gives the cue orchestral depth
  // in line with the rest of the palette without re-introducing
  // the bright triad-lift the old design relied on.
  brassHorn(c, out.node, 146.83, now + 0.018, 0.14, 0.3); // D3
  brassHorn(c, out.node, 185.0, now + 0.04, 0.12, 0.3); // F#3
}

// ─── Reward — triumphant brass fanfare + harp shimmer ───────────────────────

/**
 * Short brass stab on an F-major triad with a harp sparkle trailing off
 * the top. Reads as a triumphant "well earned" payoff without going full
 * fanfare — it still has to sit inside the lobby ambience.
 */
export function hqPlayReward() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.75 });
  if (!out) return;
  const now = c.currentTime;

  // Brass triad — F3/A3/C4 short stabs, staggered by a few ms so the
  // lowest note leads the attack.
  [174.61, 220.0, 261.63].forEach((f, i) => {
    brassHorn(c, out.node, f, now + i * 0.012, 0.32 - i * 0.04, 0.42);
  });

  // Choral pad one octave up for fullness.
  [349.23, 440.0, 523.25].forEach((f, i) => {
    stringBow(c, out.node, f, now + 0.02, 0.18 - i * 0.02, 0.12, 0.35, 0.6);
  });

  // Harp sparkle on the tonic chord (F5-A5-C6) — rising, fast.
  [698.46, 880.0, 1046.5].forEach((f, i) => {
    harpPluck(c, out.node, f, now + 0.22 + i * 0.05, 0.3 - i * 0.05, 0.6);
  });

  // Bell shimmer high above for cinematic tail.
  softBell(c, out.node, 1760.0, now + 0.38, 0.22, 1.3);
}

// ─── Fee alert — two-note harp glance ───────────────────────────────────────

/**
 * A gentle two-note harp motif, a perfect fifth apart, with a thin choir
 * pad underneath. Reads as "heads-up" without being an alarm — closer
 * to a tavern NPC clearing their throat than a system beep.
 */
export function hqPlayFeeAlert() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.55 });
  if (!out) return;
  const now = c.currentTime;

  // Two harp plucks — A4 → E5 (fifth up). Small enough gap that it
  // feels like a chord, large enough that the upward motion reads.
  harpPluck(c, out.node, 440.0, now, 0.32, 0.55);
  harpPluck(c, out.node, 659.25, now + 0.14, 0.28, 0.6);

  // Gentle pad underneath — A3 + E4, short swell.
  stringBow(c, out.node, 220.0, now, 0.14, 0.15, 0.35, 0.45);
  stringBow(c, out.node, 329.63, now, 0.12, 0.15, 0.35, 0.45);
}

// ─── Claim fees — treasure chest: coins + bells + brass bump ────────────────

/**
 * "Your gold has arrived." A soft brass bump underpins a cascade of
 * harp-like coin plucks, crowned by a bell toll. Replaces the synth
 * ka-ching with something closer to an RPG loot chime.
 */
export function hqPlayClaimFees() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.7 });
  if (!out) return;
  const now = c.currentTime;

  // Brass bump underneath — short C3 + G3 stab for weight.
  brassHorn(c, out.node, 130.81, now, 0.22, 0.32);
  brassHorn(c, out.node, 196.0, now + 0.008, 0.2, 0.32);

  // Coin cascade — five ascending harp plucks (pentatonic) panned
  // subtly left→right so the coins "pour" across the stereo field.
  const coins = [880.0, 987.77, 1174.66, 1318.51, 1568.0]; // A5, B5, D6, E6, G6
  coins.forEach((f, i) => {
    const subOut = voiceOut({
      pan: -0.25 + (i / (coins.length - 1)) * 0.5,
      reverb: 0.7,
    });
    if (!subOut) return;
    harpPluck(c, subOut.node, f, now + 0.08 + i * 0.055, 0.28 - i * 0.02, 0.5);
  });

  // Resolving bell on G5 — the "chest closes" moment.
  softBell(c, out.node, 783.99, now + 0.42, 0.3, 1.2);
}

// ─── Anti-snipe lock — low choir + tolling bell of sealing ──────────────────

/**
 * A solemn "the gate closes" cue. Dark choir pad, low bell toll, and a
 * final brass horn stab that feels like portcullis slamming down. No
 * sharp snap — we want "irreversible" not "beep".
 */
export function hqPlayAntiSnipeLock() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.65 });
  if (!out) return;
  const now = c.currentTime;

  // Dark pad — D minor (D3, F3, A3), long attack so it feels like a
  // weight settling over the room before the bell.
  [146.83, 174.61, 220.0].forEach((f, i) => {
    stringBow(c, out.node, f, now, 0.22 - i * 0.03, 0.22, 0.4, 0.8);
  });

  // Tolling bell on D3 — deep, heavy, long decay.
  softBell(c, out.node, 146.83, now + 0.12, 0.36, 1.8);

  // Brass seal — short low-mid stab right after the bell attack.
  brassHorn(c, out.node, 110.0, now + 0.22, 0.28, 0.5);

  // Sub drop — sine tail that slides down to emphasise finality.
  const sub = c.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(73.42, now + 0.2);
  sub.frequency.exponentialRampToValueAtTime(36.7, now + 0.9);
  const subEnv = envelopeGain(c, now + 0.2, 0.04, 0.42, 0.15, 0.55);
  sub.connect(subEnv).connect(out.node);
  sub.start(now + 0.2);
  sub.stop(now + 1.0);
}

// Graduation removed — the v2 grids design does not include the graduation
// concept, so the matching 3D cue has been dropped.

// ─── Chat ping — two-harp "a scroll arrives" ────────────────────────────────

/**
 * Two gentle harp plucks, a perfect fourth apart (E5 → A5). Quiet, slight
 * right pan, same reverb as the rest of the palette — a message has
 * arrived at your table, nothing more urgent than that.
 */
export function hqPlayChatPing() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.45, pan: 0.08 });
  if (!out) return;
  const now = c.currentTime;

  harpPluck(c, out.node, 659.25, now, 0.22, 0.45);
  harpPluck(c, out.node, 880.0, now + 0.09, 0.2, 0.5);
}

// ─── Board fly-through — air-rush whoosh + soft set-down landing

/**
 * The "camera sweeps over the outgoing board into apex" cue, fired
 * the moment a visible board crossfade begins. The lobby's two-slot
 * stage (`LobbyBoardStage`) schedules this at the start of the fade
 * and pairs it with `hqPlayBoardLand` after `BOARD_CROSSFADE_MS` —
 * the user hears the air-rush peak as the outgoing slot starts
 * disappearing and the soft set-down lands as the incoming slot
 * reaches opacity 1.
 *
 * Voicing — a single bandpassed noise voice with a doppler-style
 * centre-frequency tilt (low → mid → low) plus a slow sub swell
 * underneath for body. The earlier 3-voice palette (air + sub +
 * high shimmer) startled listeners on onset because the high
 * shimmer at ~1.8 kHz built sibilant content fast even with a long
 * attack envelope, and three voices stacking up cumulatively hit
 * peak gain ~0.86. This narrower, softer 2-voice palette tops out
 * around 0.48 and uses a 50–60 % attack so the swell is unmistakably
 * a fade-in rather than an onset — no jumpscare at the start, but
 * the apex still reads as a "passing-by" doppler.
 *
 * Replaces the older `hqPlayBoardSink` from the sink-into-plinth
 * mechanic; the new "Star Wars" transition is a camera fly-through,
 * not a tile fold, so the audio palette shifts from "stone slab
 * mechanism" to "air rushing past the camera".
 */
export function hqPlayBoardWhoosh() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.5 });
  if (!out) return;
  const now = c.currentTime;
  const dur = 0.9;

  // Air rush — bandpassed noise tilting 220 → 1100 → 480 Hz across
  // the cue. Lower starting band keeps the onset felt in the body
  // rather than the ear; mid-flight peak reads as the doppler apex;
  // tail eases back into the body tone for the sweep. Q=1.1 narrows
  // the band enough to dampen the broadband sibilance the prior
  // Q=0.7 air voice produced while still reading as airy whoosh.
  const air = c.createBufferSource();
  air.buffer = noiseBuffer(c, dur + 0.15);
  const airBp = c.createBiquadFilter();
  airBp.type = 'bandpass';
  airBp.frequency.setValueAtTime(220, now);
  airBp.frequency.exponentialRampToValueAtTime(1100, now + dur * 0.55);
  airBp.frequency.exponentialRampToValueAtTime(480, now + dur);
  airBp.Q.value = 1.1;
  // 50 % attack so the swell is unambiguously a fade-in. Peak gain
  // 0.30 (down from 0.42 in the prior 3-voice version). Long-ish
  // hold + decay so the tail blends into the land cue rather than
  // cutting off abruptly at the apex.
  const airEnv = envelopeGain(c, now, dur * 0.5, 0.3, dur * 0.25, dur * 0.35);
  air.connect(airBp).connect(airEnv).connect(out.node);
  air.start(now);
  air.stop(now + dur + 0.15);

  // Sub presence — 36 → 58 → 44 Hz swell underpins the air voice so
  // the whoosh has body without thumping. Even slower attack than
  // the air voice (60 % of duration) so the rumble grows
  // imperceptibly into the apex; peak gain dropped to 0.18 (from
  // 0.32) to keep the start whisper-quiet.
  const sub = c.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(36, now);
  sub.frequency.exponentialRampToValueAtTime(58, now + dur * 0.55);
  sub.frequency.exponentialRampToValueAtTime(44, now + dur);
  const subEnv = envelopeGain(c, now, dur * 0.6, 0.18, dur * 0.2, dur * 0.3);
  sub.connect(subEnv).connect(out.node);
  sub.start(now);
  sub.stop(now + dur + 0.15);
}

/**
 * The "new board lands at home framing" cue, scheduled to fire at
 * the end of the visible crossfade (so `BOARD_CROSSFADE_MS` after
 * `hqPlayBoardWhoosh`). A soft set-down: damped low-mid stone
 * *thunk* + faint settling tick that closes the transition out
 * cleanly. Roughly half the gain of the old `hqPlayBoardRise`
 * (it's a punctuation, not a finale) so the whoosh is the
 * dominant motion in the palette.
 *
 * Replaces the older `hqPlayBoardRise` from the rise-from-plinth
 * mechanic.
 */
export function hqPlayBoardLand() {
  if (isSoundMuted()) return;
  const c = getCtx();
  if (!c) return;
  const out = voiceOut({ reverb: 0.4 });
  if (!out) return;
  const now = c.currentTime;

  // Closing thunk — damped low-mid impact, slightly higher pitched
  // than the old rise's seated-slab thunk (this is a "land" not a
  // "set down"). Saw through low-pass + soft envelope; reads as the
  // camera coming to rest above the new board.
  const thunk = c.createOscillator();
  thunk.type = 'sawtooth';
  thunk.frequency.setValueAtTime(160, now);
  thunk.frequency.exponentialRampToValueAtTime(72, now + 0.2);
  const thunkLp = c.createBiquadFilter();
  thunkLp.type = 'lowpass';
  thunkLp.frequency.value = 420;
  const thunkEnv = envelopeGain(c, now, 0.004, 0.42, 0.03, 0.2);
  thunk.connect(thunkLp).connect(thunkEnv).connect(out.node);
  thunk.start(now);
  thunk.stop(now + 0.26);

  // Settling tick — short triangle harmonic at ~1.4 kHz that fades
  // off the back of the thunk. Sells the camera anchor catching as
  // the new board comes into focus. Faint so it doesn't compete
  // with the thunk.
  const tick = c.createOscillator();
  tick.type = 'triangle';
  tick.frequency.setValueAtTime(1400, now + 0.018);
  const tickEnv = envelopeGain(c, now + 0.018, 0.001, 0.05, 0.0, 0.06);
  tick.connect(tickEnv).connect(out.node);
  tick.start(now + 0.018);
  tick.stop(now + 0.1);
}
